import { S3Client, PutObjectCommand, DeleteObjectCommand, ListBucketsCommand, HeadBucketCommand } from "@aws-sdk/client-s3";

const endpoint = import.meta.env.VITE_MINIO_ENDPOINT;
const accessKeyId = import.meta.env.VITE_MINIO_ACCESS_KEY;
const secretAccessKey = import.meta.env.VITE_MINIO_SECRET_KEY;
const bucketName = import.meta.env.VITE_MINIO_BUCKET_FILES;

// Validar que el endpoint esté configurado
if (!endpoint) {
  console.error('[MinIO] ❌ VITE_MINIO_ENDPOINT no está configurado');
  throw new Error('VITE_MINIO_ENDPOINT no está configurado. Debe ser: https://eltranquerostorage.cloud');
}

// Normalizar endpoint: remover trailing slash
// El endpoint debe ser: https://eltranquerostorage.cloud (sin puerto, sin path adicional)
let normalizedEndpoint = endpoint ? endpoint.replace(/\/+$/, '') : '';

// Si el endpoint tiene un puerto (ej: :9000), removerlo
// El puerto no debe estar en la URL cuando se usa un proxy reverso
if (normalizedEndpoint && normalizedEndpoint.includes(':9000')) {
  console.warn('[MinIO] ⚠️ El endpoint contiene :9000, removiéndolo automáticamente...');
  normalizedEndpoint = normalizedEndpoint.replace(/:9000\/?$/, '').replace(/:9000/, '');
  console.warn('[MinIO] Endpoint corregido a:', normalizedEndpoint);
}

// Validar formato del endpoint
if (!normalizedEndpoint.startsWith('http://') && !normalizedEndpoint.startsWith('https://')) {
  console.error('[MinIO] ❌ ERROR: El endpoint debe comenzar con http:// o https://');
  console.error('[MinIO] Endpoint actual:', normalizedEndpoint);
  console.error('[MinIO] Endpoint correcto debe ser: https://eltranquerostorage.cloud');
} else {
  console.log('[MinIO] ✅ Endpoint configurado correctamente:', normalizedEndpoint);
}

// Configuración del cliente S3 para MinIO
const s3Client = new S3Client({
  region: "us-east-1", // Región dummy para MinIO
  endpoint: normalizedEndpoint,
  credentials: {
    accessKeyId,
    secretAccessKey,
  },
  forcePathStyle: true, // Necesario para MinIO
  // Configuración de SSL basada en VITE_MINIO_USE_SSL
  // Si el endpoint es HTTPS, usar TLS; si es HTTP, no usar TLS
  // Nota: AWS SDK maneja esto automáticamente basado en el protocolo del endpoint
});

/**
 * Sube un archivo a MinIO y retorna la URL pública (o relativa).
 * @param {File} file - Objeto File del navegador
 * @param {string} path - Ruta/prefijo opcional (ej: "ingredientes/")
 * @returns {Promise<string>} URL del archivo subido
 */
export async function uploadFileToMinIO(file, path = "") {
  if (!file) throw new Error("No se proporcionó ningún archivo");

  // Validar que el endpoint esté correctamente configurado antes de intentar subir
  if (!normalizedEndpoint || (!normalizedEndpoint.startsWith('http://') && !normalizedEndpoint.startsWith('https://'))) {
    const errorMsg = `VITE_MINIO_ENDPOINT está mal configurado. Actual: "${normalizedEndpoint || 'no configurado'}". Debe ser: "https://eltranquerostorage.cloud". Por favor, corrige esta variable en Dokploy y reconstruye la app.`;
    console.error('[MinIO] ❌', errorMsg);
    throw new Error(errorMsg);
  }

  // Verificar que el bucket existe y tenemos acceso antes de intentar subir
  // NOTA: HeadBucket puede fallar por permisos, pero eso no significa que PutObject no funcione
  // Por eso continuamos incluso si falla
  try {
    console.log('[MinIO] Verificando acceso al bucket:', bucketName);
    const headCommand = new HeadBucketCommand({ Bucket: bucketName });
    await s3Client.send(headCommand);
    console.log('[MinIO] ✅ Bucket accesible:', bucketName);
  } catch (bucketErr) {
    console.warn('[MinIO] ⚠️ No se pudo verificar el bucket (esto puede ser normal si HeadBucket no está permitido):', bucketErr.message || bucketErr);
    // Continuar de todas formas, puede ser un problema de permisos de HeadBucket
    // pero PutObject podría funcionar
  }

  const fileName = `${path}${Date.now()}-${file.name.replace(/\s+/g, "-")}`;

  // Convertir File a ArrayBuffer/Uint8Array para subirlo
  const arrayBuffer = await file.arrayBuffer();
  const body = new Uint8Array(arrayBuffer);

  const command = new PutObjectCommand({
    Bucket: bucketName,
    Key: fileName,
    Body: body,
    ContentType: file.type,
  });

  try {
    console.log('[MinIO] Subiendo archivo:', {
      endpoint: normalizedEndpoint,
      bucket: bucketName,
      key: fileName,
      method: 'PUT',
      contentType: file.type,
      accessKeyId: accessKeyId ? accessKeyId.substring(0, 10) + '...' : 'no configurado'
    });

    // Construir la URL completa que se usará
    const fullUrl = `${normalizedEndpoint}/${bucketName}/${fileName}`;
    console.log('[MinIO] URL completa:', fullUrl);

    const response = await s3Client.send(command);
    // Retornar la URL. Construimos la URL manualmente.
    const url = `${normalizedEndpoint}/${bucketName}/${fileName}`;
    console.log('[MinIO] ✅ Archivo subido exitosamente:', url);
    return url;
  } catch (err) {
    console.error("❌ Error subiendo a MinIO:", err);
    console.error('[MinIO] Detalles del error:', {
      message: err.message,
      name: err.name,
      code: err.code,
      statusCode: err.$response?.statusCode,
      requestId: err.$metadata?.requestId,
      endpoint: normalizedEndpoint
    });

    // Intentar obtener el cuerpo de la respuesta para debugging
    if (err.$response) {
      try {
        const body = err.$response.body;
        if (body) {
          const bodyStr = typeof body === 'string' ? body : JSON.stringify(body);
          console.error('[MinIO] Respuesta del servidor:', bodyStr.substring(0, 500));
          if (bodyStr.includes('<!DOCTYPE') || bodyStr.includes('<html>')) {
            console.error('[MinIO] ⚠️ El servidor devolvió HTML en lugar de XML. Esto indica que el proxy no está funcionando correctamente.');
          }
        }
      } catch (e) {
        console.error('[MinIO] No se pudo leer el cuerpo de la respuesta:', e);
      }
    }

    // Logging adicional para errores de acceso
    if (err.name === 'AccessDenied' || err.code === 'AccessDenied' || (err.$response?.statusCode === 403)) {
      console.error('[MinIO] 🔐 Error de acceso (403 Forbidden) - Verificar:');
      console.error('[MinIO] 1. Credenciales en Dokploy deben ser:');
      console.error('[MinIO]    VITE_MINIO_ACCESS_KEY=tranquero-app');
      console.error('[MinIO]    VITE_MINIO_SECRET_KEY=tranquero123456');
      console.error('[MinIO] 2. Que el usuario "tranquero-app" exista en MinIO');
      console.error('[MinIO] 3. Que el usuario tenga la política "tranquero-app-policy" asignada');
      console.error('[MinIO] 4. Que el bucket exista:', bucketName);
      console.error('[MinIO] 5. ACCESS_KEY usado:', accessKeyId ? accessKeyId.substring(0, 10) + '...' : 'no configurado');
      console.error('[MinIO]');
      console.error('[MinIO] 📋 SOLUCIÓN:');
      console.error('[MinIO] - Accede a https://eltranquerostorage.cloud/console/');
      console.error('[MinIO] - Inicia sesión con admin/admin123456');
      console.error('[MinIO] - Ve a Identity → Users y verifica que "tranquero-app" exista');
      console.error('[MinIO] - Si no existe, reinicia el servicio "minio-client" en Dokploy');
      console.error('[MinIO] - Verifica que el bucket "tranquero-files" exista en Buckets');
    }

    // Mejorar mensaje de error con más detalles
    let errorMessage = err.message || 'Error desconocido';
    if (err.$response) {
      const statusCode = err.$response?.statusCode;
      errorMessage = `Error ${statusCode || 'HTTP'}: ${errorMessage}`;

      if (statusCode === 405) {
        errorMessage += ' (Method Not Allowed - el proxy no está permitiendo PUT/POST. Verificar configuración de Traefik/Nginx)';
      } else if (statusCode === 403) {
        errorMessage += ' (Forbidden - verificar credenciales de MinIO)';
      } else if (statusCode === 404) {
        errorMessage += ' (Not Found - verificar que el bucket exista, que el usuario tenga permisos, y que la URL del endpoint sea correcta)';
        console.error('[MinIO] 🔍 Diagnóstico 404:');
        console.error('[MinIO] - Bucket esperado:', bucketName);
        console.error('[MinIO] - Endpoint:', normalizedEndpoint);
        console.error('[MinIO] - URL completa:', `${normalizedEndpoint}/${bucketName}/${fileName}`);
        console.error('[MinIO] - Verificar en consola de MinIO que el bucket "' + bucketName + '" exista');
        console.error('[MinIO] - Verificar que el usuario tenga permisos s3:ListBucket y s3:PutObject');
      }
    }

    throw new Error(`Error al subir imagen a MinIO: ${errorMessage}`);
  }
}

/**
 * Elimina un archivo de MinIO.
 * @param {string} key - Clave del archivo (ej: "ingredientes/foto.jpg")
 */
export async function deleteFileFromMinIO(key) {
  if (!key) return;

  const command = new DeleteObjectCommand({
    Bucket: bucketName,
    Key: key,
  });

  try {
    await s3Client.send(command);
    console.log(`Archivo eliminado de MinIO: ${key}`);
  } catch (err) {
    console.error("Error eliminando de MinIO:", err);
    throw new Error(`Error al eliminar imagen de MinIO: ${err.message}`);
  }
}
